import { makeStyles } from "@mui/styles";
import React from "react";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import NoTimeOffRequestImage from "../components/NoTimeOffRequestsImage";
import { format_ShortDOW_Date, getShiftTime, parsePreferredLeaveTime } from "../util/dateTimeFormatter";
import { getTOREmployeeName } from "../util/timeOffExportMapper";
import { EMPLOYEE_TEMP_LABEL } from "../util/constants";
import { isLWOPTOWOPAllDayRequest } from "../util/timeOffRequestUtils";
import { selectUserSession } from "../slices/userSession/userSessionSlice";

const useStyles = makeStyles((theme) => ({
  pageTitle: {
    fontSize: "24px",
    textAlign: "left",
    width: "212px",
    margin: "auto",
    marginTop: "10px",
    marginLeft: "20px",
    whiteSpace: "normal",
  },
  torListItem: {
    margin: "10px",
    padding: "10px",
    textAlign: "left",
    background: theme.palette.secondary.swaGray1,
  },
  torListOwnItem: {
    margin: "10px",
    padding: "10px",
    textAlign: "left",
    background: theme.palette.secondary.swaLightestBlue,
  },
  requestFont: {
    fontSize: "12px",
  },
  status: {
    color: theme.palette.primary.swaBlue,
  },
}));

const EmployeeTimeOffWOPMobilePage = (props) => {
  //#region Constants
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const isAdminPage = false;
  //#endregion

  const getSubmittedDate = (torRequest) => {
    const submittedDate = torRequest.submittedDate ? torRequest.submittedDate : torRequest.approvedDate;
    return format_ShortDOW_Date(submittedDate) + " " + getShiftTime(submittedDate);
  };

  //#region Component Renders
  const renderEmployee = (timeOffRequest) => {
    return (
      <>
        {getTOREmployeeName(timeOffRequest)} ({timeOffRequest.empId})
        {timeOffRequest.isTemp && <Chip sx={{ marginLeft: "5px" }} label={EMPLOYEE_TEMP_LABEL} />}
      </>
    );
  };

  const renderShiftTime = (timeOffRequest) => {
    const shift =
      format_ShortDOW_Date(timeOffRequest.shiftStart) +
      " " +
      getShiftTime(timeOffRequest.shiftStart) +
      "-" +
      getShiftTime(timeOffRequest.shiftEnd);
    if (!isLWOPTOWOPAllDayRequest(timeOffRequest.timeOffRequestType)) {
      const parsedPreferredLeaveTime = parsePreferredLeaveTime(
        timeOffRequest.preferredWOPStart,
        timeOffRequest.preferredWOPEnd
      );
      const preferredLeaveTime = timeOffRequest.wopASAP ? "ASAP" : parsedPreferredLeaveTime;
      return (
        <div>
          <b>{preferredLeaveTime}</b> (Shift: {shift})
        </div>
      );
    } else return shift;
  };

  const renderApprovedLeaveTime = (timeOffRequest) => {
    if (timeOffRequest.approvedWOPStart && timeOffRequest.approvedWOPEnd) {
      const parsedApprovedLeaveTime = parsePreferredLeaveTime(
        timeOffRequest.approvedWOPStart,
        timeOffRequest.approvedWOPEnd
      );

      return <div>Approved Leave Time: {parsedApprovedLeaveTime}</div>;
    }
  };

  const renderMobileEmployeeTimeOffWOPPage = () => {
    return (
      <div className="employee-time-off-wop-mobile-page">
        <Grid container justifyContent="center" spacing={1}>
          <Grid container className="mobile-title-row">
            <Typography component={"div"}>
              <Box className={classes.pageTitle}>LWOP/TOWOP List</Box>
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Box mt={1} mb={1}>
              <FormControl variant="outlined">{props.wopTypeSelect}</FormControl>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box mt={1} mb={1}>
              {props.searchInput}
            </Box>
          </Grid>
        </Grid>
        <Grid container>
          <Box
            sx={{
              bgcolor: "secondary.swaLightestBlue",
              width: "100%",
              height: "60px",
              borderTopLeftRadius: "5px",
              borderTopRightRadius: "5px",
              borderTop: "solid 1px",
              borderLeft: "solid 1px",
              borderRight: "solid 1px",
              borderColor: "secondary.swaGray2",
              borderRadius: 0,
            }}
          >
            <Stack
              sx={{ height: "100%", justifyContent: "left", alignItems: "center", padding: "15px" }}
              direction="row"
              divider={<Divider sx={{ marginTop: "5px", marginBottom: "5px" }} orientation="vertical" flexItem />}
            >
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item xs={7} sm={7}></Grid>
                <Grid item xs={5} sm={5}>
                  <Button
                    style={{ backgroundColor: "white", paddingLeft: 8, paddingRight: 8 }}
                    size="small"
                    fullWidth
                    onClick={props.handleClickMobileFilterDialogOpen.bind(this)}
                    sx={{ border: "solid 0.5px", borderColor: "secondary.swaBlue" }}
                  >
                    <Grid
                      container
                      spacing={1}
                      justifyContent="space-between"
                      alignItems="center"
                      alignContent="center"
                    >
                      <Grid item alignItems="center" xs={3} sm={2} md={1}>
                        <FilterAltIcon sx={{ marginTop: 1, fontSize: "18px" }} />
                      </Grid>
                      <Grid item xs={9} sm={10} md={11}>
                        <Typography sx={{ fontSize: "14px" }}>Filters</Typography>
                      </Grid>
                    </Grid>
                  </Button>
                </Grid>
              </Grid>
            </Stack>
          </Box>
          <Grid container >
              <Grid item xs={12} sx={{marginTop: "5px"}}>
                {props.renderRequestWOPButton()}
              </Grid>
            </Grid>
        </Grid>
        <div className="enable-scroll">
          {props.isLoading ? (
            <CircularProgress />
          ) : props.timeOffRequests.length === 0 ? (
            <NoTimeOffRequestImage
              isAdminPage={isAdminPage}
              isWOPTable={true}
              isWOPListFrozen={props.isWOPListFrozen}
              isDesktopView={props.isDesktopView}
            />
          ) : (
            <div>
              {props.timeOffRequests.map((torRequest) => {
                return (
                  <Paper
                    key={torRequest.id}
                    className={torRequest.empId === userSession.eid ? classes.torListOwnItem : classes.torListItem}
                    elevation={1}
                    data-testid={torRequest.id}
                  >
                    {
                      <div>
                        <div style={{ fontSize: "16px", fontWeight: "bold" }}>
                          {renderEmployee(torRequest)} | <b className={classes.status}>{torRequest.visibleStatus}</b>
                        </div>
                        <div className={classes.requestFont}>{`Seniority Rank: ${torRequest.rank}`}</div>
                        <div className={classes.requestFont}>{renderShiftTime(torRequest)}</div>
                        <div className={classes.requestFont}>{renderApprovedLeaveTime(torRequest)}</div>
                        <div className={classes.requestFont}>{`Submitted on ${getSubmittedDate(torRequest)}`}</div>
                      </div>
                    }
                  </Paper>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  return <div data-testid="employee-time-off-wop-mobile-page">{renderMobileEmployeeTimeOffWOPPage()}</div>;
  //#endregion
};

EmployeeTimeOffWOPMobilePage.propTypes = {
  timeOffRequests: PropTypes.array.isRequired,
  wopTypeSelect: PropTypes.element.isRequired,
  searchInput: PropTypes.element.isRequired,
  isWOPListFrozen: PropTypes.bool.isRequired,
  handleClickMobileFilterDialogOpen: PropTypes.func.isRequired,
  renderRequestWOPButton: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
};

export default EmployeeTimeOffWOPMobilePage;
